import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { WifiOff, Wifi, RefreshCw, CloudOff } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { getPendingCount, syncOfflineEntries, clearAllPendingEntries } from "../lib/offlineDB";

export const OfflineSyncManager = () => {
  const { api } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const apiRef = useRef(api);

  // Keep apiRef current
  useEffect(() => { apiRef.current = api; }, [api]);

  const refreshCount = useCallback(() => {
    getPendingCount().then(setPendingCount);
  }, []);

  const handleSync = useCallback(async () => {
    const currentApi = apiRef.current;
    if (!currentApi || !navigator.onLine) return;
    setSyncing(true);
    try {
      // Use raw API to avoid offline wrapper re-saving on failure
      const rawApi = currentApi._rawApi || currentApi;
      const result = await syncOfflineEntries(rawApi);
      if (result.synced > 0) toast.success(`${result.synced} offline entries synced!`);
      if (result.failed > 0) toast.error(`${result.failed} entries sync failed`);
      refreshCount();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  }, [refreshCount]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setReconnecting(true);
      toast.success("Connection restored. Checking pending entries...");
      Promise.resolve(handleSync()).finally(() => setReconnecting(false));
    };
    const handleOffline = () => {
      setIsOnline(false);
      setReconnecting(false);
      toast("You are offline. Entries will be saved locally.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const interval = setInterval(refreshCount, 5000);
    refreshCount();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, [handleSync, refreshCount]);

  if (pendingCount === 0 && isOnline && !reconnecting) return null;

  return (
    <div className="fixed bottom-20 right-3 z-50 flex max-w-[calc(100vw-1.5rem)] flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 shadow-lg lg:bottom-4 lg:right-4" data-testid="offline-sync-bar">
      {!isOnline ? (
        <Badge variant="outline" className="gap-1 border-red-500/30 bg-red-500/10 text-red-400">
          <WifiOff className="w-3 h-3" /> Offline
        </Badge>
      ) : reconnecting ? (
        <Badge variant="outline" className="gap-1 border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid="connection-reconnecting">
          <RefreshCw className="h-3 w-3 animate-spin" /> Reconnecting
        </Badge>
      ) : (
        <Badge variant="outline" className="gap-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
          <Wifi className="w-3 h-3" /> Online
        </Badge>
      )}
      {pendingCount > 0 && (
        <>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 gap-1">
            <CloudOff className="w-3 h-3" /> {pendingCount} pending
          </Badge>
          {isOnline && (
            <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing} className="h-7 text-xs" data-testid="sync-now-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              Sync
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await clearAllPendingEntries();
              refreshCount();
              toast.success("Pending entries cleared");
            }}
            className="h-7 text-xs text-muted-foreground hover:text-destructive"
            data-testid="clear-pending-btn"
          >
            Clear
          </Button>
        </>
      )}
    </div>
  );
};
